import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle2, ShieldCheck, Mail, ArrowLeft } from 'lucide-react'
import { getConsentimientoPublico, enviarCodigoConsentimiento, firmarConsentimientoPublico, renderAvisoClub, renderLegalDoc, LEGAL_DOC_KEYS } from '../lib/supabase'
import LegalDocLink from './legal/LegalDocLink'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { correoValido } from '../lib/email'

// Página pública (sin login) para que el tutor / jugador firme el consentimiento.
// Se abre con el token del link compartido (wa.me / correo).
//
// Son DOS pasos a propósito. El enlace por sí solo no prueba quién lo abrió -- se
// reenvía -- y para datos de menores eso no basta. Así que el tutor declara su
// correo, recibe un código y lo teclea: hasta entonces no hay firma.
//
// El correo se pide AQUÍ y no antes porque casi nadie lo tiene capturado (1 de 207
// jugadores). Pedirlo en el momento de firmar lo consigue sin campaña previa, y
// además llega verificado, que vale más que uno tecleado por un admin.
const PASO_DATOS = 'datos'
const PASO_CODIGO = 'codigo'

const PARENTESCOS = ['Madre', 'Padre', 'Tutor legal', 'Abuelo o abuela', 'Otro']

// OJO: este marco tiene que vivir FUERA del componente.
//
// Definido adentro, cada render crea una funcion NUEVA -- para React es un tipo de
// componente distinto, asi que desmonta todo el arbol y lo vuelve a montar. El
// sintoma: escribes una letra y el campo pierde el foco. No se notaba mientras la
// pantalla solo tenia casillas; aparecio al agregarle campos de texto.
const Marco = ({ info, children }) => (
  <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
    <div className="w-full max-w-lg bg-background rounded-2xl border shadow-sm p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        {info?.club_logo ? (
          <img src={info.club_logo} alt={info.club} className="size-10 rounded object-contain" />
        ) : (
          <ShieldCheck className="size-8 text-primary" />
        )}
        <div>
          <p className="text-xs text-muted-foreground">Consentimiento</p>
          <p className="font-semibold text-foreground">{info?.club || 'Club'}</p>
        </div>
      </div>
      {children}
    </div>
  </div>
)

const ConsentimientoFirma = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)
  const [firmado, setFirmado] = useState(false)
  const [paso, setPaso] = useState(PASO_DATOS)
  const [enviando, setEnviando] = useState(false)
  const [saving, setSaving] = useState(false)

  // Paso 1
  const [nombre, setNombre] = useState('')
  const [parentesco, setParentesco] = useState('')
  const [correo, setCorreo] = useState('')
  const [trata, setTrata] = useState(false)
  const [imagen, setImagen] = useState(false)

  // Paso 2
  const [codigo, setCodigo] = useState('')

  useEffect(() => {
    let cancel = false
    getConsentimientoPublico(token).then(({ data, error }) => {
      if (cancel) return
      if (error || !data || data.error) {
        setErrorMsg('Este enlace no es válido o ya expiró.')
      } else {
        setInfo(data)
        if (data.estado === 'firmado') setFirmado(true)
      }
      setLoading(false)
    })
    return () => { cancel = true }
  }, [token])

  const esTutor = info?.consiente_tipo === 'tutor'
  const jugadores = info?.jugadores || []

  // El spec pide que el titular VEA la carta antes de marcar nada. Se arman con los
  // datos legales del club, que `get_consentimiento_publico` devuelve. Desde la pieza 2b el
  // Aviso no depende de casillas: lo que se comparte en torneos es fijo.
  const carta = info
    ? renderLegalDoc(LEGAL_DOC_KEYS.CARTA_CONSENTIMIENTO, { '[NOMBRE DEL CLUB]': info.club })
    : null
  const aviso = info
    ? renderAvisoClub({
        name: info.club,
        razon_social: info.club_razon_social,
        domicilio_fiscal: info.club_domicilio,
        correo_privacidad: info.club_correo_arco,
      })
    : null
  const puedeEnviar = trata && nombre.trim() && correoValido(correo) && (!esTutor || parentesco)

  const handleEnviarCodigo = async () => {
    if (!puedeEnviar) return
    setEnviando(true)
    setErrorMsg(null)
    const { error } = await enviarCodigoConsentimiento(token, correo.trim())
    setEnviando(false)
    if (error) { setErrorMsg(error.message); return }
    setCodigo('')
    setPaso(PASO_CODIGO)
  }

  const handleFirmar = async () => {
    if (codigo.trim().length < 6) return
    setSaving(true)
    setErrorMsg(null)
    const { error } = await firmarConsentimientoPublico(token, {
      codigo: codigo.trim(),
      trataDatos: trata,
      usaImagen: imagen,
      // Va dentro del bloque obligatorio de la carta, no como casilla aparte.
      autorizaMyklubb: trata,
      consienteNombre: nombre.trim(),
      consienteParentesco: esTutor ? parentesco : null,
      // Las versiones de lo que esta pagina mostro (los mismos objetos que enlaza), no las
      // vigentes: la Edge solo firma si coinciden, para que la prueba diga lo que se leyo.
      avisoVersion: aviso?.version ?? null,
      cartaVersion: carta?.version ?? null,
    })
    setSaving(false)
    if (error) { setErrorMsg(error.message); return }
    setFirmado(true)
  }

  if (loading) {
    return <Marco info={info}><p className="text-muted-foreground text-sm py-8 text-center">Cargando…</p></Marco>
  }

  if (errorMsg && !info) {
    return <Marco info={info}><p className="text-destructive text-sm py-8 text-center">{errorMsg}</p></Marco>
  }

  if (firmado) {
    return (
      <Marco info={info}>
        <div className="text-center py-6">
          <CheckCircle2 className="size-12 text-green-600 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-foreground mb-1">¡Consentimiento registrado!</h1>
          <p className="text-sm text-muted-foreground">
            Gracias. Ya puedes cerrar esta ventana.
          </p>
        </div>
      </Marco>
    )
  }

  if (paso === PASO_CODIGO) {
    return (
      <Marco info={info}>
        <button
          type="button"
          onClick={() => { setPaso(PASO_DATOS); setErrorMsg(null) }}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="size-4" /> Volver
        </button>

        <div className="text-center mb-5">
          <Mail className="size-10 text-primary mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-foreground mb-1">Revisa tu correo</h1>
          <p className="text-sm text-muted-foreground">
            Mandamos un código de 6 dígitos a <strong className="text-foreground">{correo.trim()}</strong>.
            Escríbelo para completar la firma.
          </p>
        </div>

        <div className="space-y-2 mb-4">
          <Label htmlFor="codigo">Código de verificación</Label>
          <Input
            id="codigo"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="text-center text-2xl tracking-[0.4em] font-semibold h-14"
          />
        </div>

        {errorMsg && <p className="text-destructive text-sm mb-3">{errorMsg}</p>}

        <Button className="w-full" disabled={codigo.trim().length < 6 || saving} onClick={handleFirmar}>
          {saving ? 'Registrando…' : 'Firmar consentimiento'}
        </Button>

        <button
          type="button"
          onClick={handleEnviarCodigo}
          disabled={enviando}
          className="w-full text-sm text-muted-foreground hover:text-foreground mt-3 disabled:opacity-50"
        >
          {enviando ? 'Enviando…' : '¿No te llegó? Enviar otro código'}
        </button>
        <p className="text-[11px] text-muted-foreground mt-3 text-center">
          Revisa también la carpeta de correo no deseado.
        </p>
      </Marco>
    )
  }

  return (
    <Marco info={info}>
      <h1 className="text-lg font-semibold text-foreground mb-2">
        Autorización y consentimiento
      </h1>
      <p className="text-sm text-muted-foreground mb-4">
        {esTutor
          ? 'Como padre, madre o tutor, autoriza el tratamiento de los datos personales del/los siguiente(s) jugador(es):'
          : 'Autoriza el tratamiento de tus datos personales como jugador:'}
      </p>

      <ul className="mb-4 rounded-md border bg-muted/40 divide-y">
        {jugadores.map((j, i) => (
          <li key={i} className="px-3 py-2 text-sm font-medium">{j.nombre}</li>
        ))}
      </ul>

      {/* Las declaraciones del bloque obligatorio de la carta 1.1 (cinco para el tutor).
          Antes esto era un parrafo de redaccion propia: el tutor aceptaba un
          documento que no podia leer. */}
      <div className="mb-4 rounded-md border p-4">
        <p className="text-sm text-foreground mb-2">
          {esTutor
            ? 'Como padre, madre o tutor del menor, manifiesto que:'
            : 'Como jugador mayor de edad, manifiesto que:'}
        </p>
        <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-5">
          {esTutor && <li>Cuento con la <strong>representación legal</strong> del menor.</li>}
          <li>He leído y acepto el <strong>Aviso de Privacidad</strong> del club.</li>
          <li>
            <strong>Autorizo el tratamiento</strong> de los datos para inscripción,
            identificación, gestión deportiva, participación en entrenamientos, partidos,
            ligas y torneos, y administración de cuotas.
          </li>
          <li>
            <strong>Autorizo que</strong>, si el club inscribe al jugador en un torneo o liga,
            comparta con la organizadora su nombre, número, categoría, fecha de nacimiento, CURP y
            fotografía, solo para verificar su identidad y su elegibilidad y nunca para difusión.
            Los demás clubes del torneo ven su nombre y su número, y el club contra el que juega
            cada partido, también su fotografía, solo desde dos días antes del partido y hasta el
            final de ese día, para verificar su identidad.
          </li>
          <li>
            <strong>Autorizo a MYKLUBB</strong> a mantener un registro de identidad unificado
            por CURP, de uso interno, para reconocer al atleta a lo largo de su trayectoria,
            evitar registros duplicados y dar seguimiento a su desarrollo. Ningún club accede
            a información que corresponda a otro club, salvo que yo lo autorice.
          </li>
        </ol>
      </div>

      <div className="flex flex-wrap gap-4 mb-5 text-sm">
        {carta && <LegalDocLink doc={carta} label="Leer la carta completa →" className="text-primary underline hover:opacity-80" />}
        {aviso && <LegalDocLink doc={aviso} label="Leer el Aviso de Privacidad →" className="text-primary underline hover:opacity-80" />}
      </div>

      <div className="space-y-4 mb-5">
        <div className="space-y-2">
          <Label htmlFor="nombre">
            Tu nombre completo <span className="text-destructive">*</span>
          </Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Como aparece en tu identificación"
          />
        </div>

        {esTutor && (
          <div className="space-y-2">
            <Label htmlFor="parentesco">
              Parentesco con el jugador <span className="text-destructive">*</span>
            </Label>
            <select
              id="parentesco"
              value={parentesco}
              onChange={(e) => setParentesco(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Selecciona…</option>
              {PARENTESCOS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="correo">
            Tu correo electrónico <span className="text-destructive">*</span>
          </Label>
          <Input
            id="correo"
            type="email"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            placeholder="correo@ejemplo.com"
          />
          <p className="text-xs text-muted-foreground">
            Te mandaremos un código para confirmar que eres tú. Sin ese paso la firma no
            queda registrada.
          </p>
        </div>
      </div>

      {/* Una sola casilla obligatoria, como en la carta: las declaraciones
          de arriba van juntas. La autorización a MYKLUBB no es opcional ahí -- y
          ofrecerla como casilla suelta sería peor que no ofrecerla: el tutor podría
          decir "no" a algo que la plataforma hace de todos modos para agrupar
          fichas por CURP, y quedaría registrada una negativa que no se respeta. */}
      <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer mb-3">
        <input
          type="checkbox"
          className="h-4 w-4 mt-0.5 rounded border-input"
          checked={trata}
          onChange={(e) => setTrata(e.target.checked)}
        />
        <span className="text-sm text-foreground">
          Acepto y autorizo el tratamiento de los datos conforme a los puntos anteriores.
          <span className="text-destructive"> *</span>
        </span>
      </label>

      <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer mb-5">
        <input
          type="checkbox"
          className="h-4 w-4 mt-0.5 rounded border-input"
          checked={imagen}
          onChange={(e) => setImagen(e.target.checked)}
        />
        <span className="text-sm text-muted-foreground">
          <span className="text-foreground">Opcional:</span> autorizo el uso de imagen (fotografía)
          para difusión en redes sociales y materiales del club. Revocable en cualquier momento.
          <span className="block mt-1">Negarlo no afecta la inscripción.</span>
        </span>
      </label>

      {errorMsg && <p className="text-destructive text-sm mb-3">{errorMsg}</p>}

      <Button className="w-full" disabled={!puedeEnviar || enviando} onClick={handleEnviarCodigo}>
        {enviando ? 'Enviando código…' : 'Continuar'}
      </Button>
      <p className="text-[11px] text-muted-foreground mt-3 text-center">
        Se registra la fecha, el correo verificado y la versión del documento como prueba
        del consentimiento.
      </p>
    </Marco>
  )
}

export default ConsentimientoFirma
